import { useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { BancoFormData, bancoSchema } from "./schema/cadastro-bancario.schema";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import {
  Tabs,
  Tab,
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Typography,
  Button,
  MenuItem,
  Icon,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import { cadastroBancarioService } from "./service/cadastro-bancario.service";
import useLoadingStore from "../../shared/store/loading.store";
import { toastError } from "../../shared/services/errors/erros-handler";
import { AgenciaContasFormPart } from "./components/agencia";
import { useNavigate } from "react-router-dom";
import { findFirstErrorMessage } from "../../shared/utils/form/find-first-error-message";

const PAGINAS = [
  { label: "Dados do Banco", value: 0 },
  { label: "Agências e Contas", value: 1 },
];

export function CriarCadastroBancario() {
  const methods = useForm<BancoFormData>({
    resolver: yupResolver(bancoSchema),
    defaultValues: {
      agencies: [],
    },
  });

  const { setLoading } = useLoadingStore();
  const [step, setStep] = useState(0);
  const [expanded, setExpanded] = useState<number | false>(0);

  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
  } = methods;

  const {
    fields: agencies,
    append: appendAgency,
    remove: removeAgency,
  } = useFieldArray({
    control,
    name: "agencies",
  });

  const navigate = useNavigate();

  const onSubmit = async (data: BancoFormData) => {
    setLoading(true, "Salvando cadastro...");
    try {
      await cadastroBancarioService.criar(data);
      toast.success("Banco cadastrado com sucesso!");
      navigate("/empresas/cadastro-bancario");
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  // Exibe o primeiro erro de validação e abre a aba onde ele está, quando o submit é bloqueado pelo schema
  const onInvalidSubmit = (formErrors: typeof errors) => {
    setStep(formErrors.agencies ? 1 : 0);
    const primeiraMensagem = findFirstErrorMessage(formErrors);
    toast.error(primeiraMensagem || "Existem campos inválidos no formulário.");
  };

  const handleAccordionChange = (idx: number) => {
    if (agencies.length === 1) {
      setExpanded(0);
    } else {
      setExpanded(expanded === idx ? false : idx);
    }
  };

  return (
    <BaseLayout title="Cadastro de Banco">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit(onSubmit, onInvalidSubmit)}
        aoClicarBotaoVoltar={() => window.history.back()}
      />

      <Tabs
        value={step}
        onChange={(_, newValue) => setStep(newValue)}
        sx={{ borderBottom: "1px solid #eee", mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {PAGINAS.map((pagina) => (
          <Tab key={pagina.value} label={pagina.label} value={pagina.value} />
        ))}
      </Tabs>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px )"
      >
        {step === 0 && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Nome do Banco"
              {...register("name")}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
            />
            <TextField
              label="Código do Banco"
              {...register("code")}
              error={!!errors.code}
              helperText={errors.code?.message}
              fullWidth
            />
            <TextField
              label="Situação"
              select
              {...register("active")}
              SelectProps={{ native: true }}
              error={!!errors.active}
              helperText={errors.active?.message}
              fullWidth
            >
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </TextField>
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Box mb={2}>
              <Button
                variant="contained"
                onClick={() =>
                  appendAgency({
                    agencyNumber: "",
                    digit: "",
                    name: "",
                    active: true,
                    accounts: [],
                  })
                }
              >
                Adicionar Agência
              </Button>
            </Box>
            {agencies.map((agency, idx) => (
              <Accordion
                key={agency.id || idx}
                expanded={expanded === idx || agencies.length === 1}
                onChange={() => handleAccordionChange(idx)}
                disabled={agencies.length === 1}
              >
                <AccordionSummary
                  expandIcon={agencies.length > 1 ? <ExpandMoreIcon /> : null}
                  aria-controls={`panel${idx}-content`}
                  id={`panel${idx}-header`}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography>
                      Agência {agency.agencyNumber || idx + 1}
                    </Typography>
                    {agencies.length > 1 && (
                      <Tooltip title="Remover Agência">
                        <IconButton
                          size="medium"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeAgency(idx);
                          }}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    display="grid"
                    gridTemplateColumns="1fr 1fr 1fr 1fr"
                    gap={2}
                  >
                    <Controller
                      name={`agencies.${idx}.agencyNumber`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Número da Agência"
                          {...field}
                          error={!!errors.agencies?.[idx]?.agencyNumber}
                          helperText={
                            errors.agencies?.[idx]?.agencyNumber?.message
                          }
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name={`agencies.${idx}.digit`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Dígito"
                          {...field}
                          error={!!errors.agencies?.[idx]?.digit}
                          helperText={errors.agencies?.[idx]?.digit?.message}
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name={`agencies.${idx}.name`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Nome da Agência"
                          {...field}
                          error={!!errors.agencies?.[idx]?.name}
                          helperText={errors.agencies?.[idx]?.name?.message}
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name={`agencies.${idx}.active`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Situação"
                          select
                          {...field}
                          SelectProps={{ native: true }}
                          error={!!errors.agencies?.[idx]?.active}
                          helperText={errors.agencies?.[idx]?.active?.message}
                          fullWidth
                        >
                          <option value="true">Ativa</option>
                          <option value="false">Inativa</option>
                        </TextField>
                      )}
                    />
                  </Box>
                  <AgenciaContasFormPart
                    control={control}
                    idx={idx}
                    errors={errors}
                    methods={methods}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>
    </BaseLayout>
  );
}
